import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { lightTheme } from '../helfer/types';

/**
 * Kopf und Fusszeile für die öffentlichen Verteiler-Seiten (#384).
 *
 * Die Selbstanmeldung (`/verteiler/…`) und die Abbestellen-Seite (`/abbestellen/…`) hatten
 * als einzige öffentliche Ansichten **kein Branding** — kein Logo, kein Name. Das war ein
 * Versäumnis aus #380, keine Entscheidung: `OrgMeldelink`, `LegalPage`, `PublicView`,
 * `StructurePublicView` und die LES-Ansicht laden es alle.
 *
 * Es faellt hier besonders ins Gewicht, weil beide Seiten Menschen empfangen, die **aus einer
 * E-Mail** kommen. Traegt die Mail das Logo und die Seite dahinter nichts, wirkt der Bruch
 * genau dort, wo Vertrauen zaehlt — beim Abbestellen ist ohnehin jeder etwas misstrauisch.
 *
 * Scheitert der Abruf, bleibt der Kopf leer. Ein fehlendes Logo darf die Seite nicht
 * blockieren; sie funktioniert auch ohne.
 */
const theme = lightTheme;

interface Branding {
  orgName: string;
  logoLight: string | null;
  logoLightMime: string | null;
  subtitle: string | null;
}

export const PublicBrandHeader: React.FC = () => {
  const [branding, setBranding] = useState<Branding | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    axios.get('/api/branding')
      .then(res => {
        if (abgebrochen) return;
        const b = res.data?.data;
        if (b?.orgName) {
          setBranding({
            orgName: b.orgName,
            logoLight: b.logoLight ?? null,
            logoLightMime: b.logoLightMime ?? null,
            subtitle: b.subtitle ?? null,
          });
        }
      })
      .catch(() => { /* ohne Branding weiter — die Seite funktioniert auch so */ });
    return () => { abgebrochen = true; };
  }, []);

  if (!branding) return null;

  const hatLogo = Boolean(branding.logoLight && branding.logoLightMime);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 6, marginBottom: 20, textAlign: 'center',
    }}>
      {hatLogo ? (
        <img
          src={`data:${branding.logoLightMime};base64,${branding.logoLight}`}
          alt={branding.orgName}
          style={{ maxHeight: 52, maxWidth: 260, height: 'auto', width: 'auto' }}
        />
      ) : (
        <div style={{ fontSize: '1.05rem', fontWeight: 700, color: theme.text }}>
          {branding.orgName}
        </div>
      )}
      {branding.subtitle && (
        <div style={{ fontSize: '.82rem', color: theme.textMuted }}>{branding.subtitle}</div>
      )}
    </div>
  );
};

/**
 * Impressum und Datenschutz (#384).
 *
 * **Pflichtangaben, kein Beiwerk.** Beide Seiten sind ohne Anmeldung erreichbar; in
 * Deutschland verlangt § 5 DDG dort ein Impressum, die DSGVO eine Datenschutzerklärung.
 * Sie fehlten als einzigen öffentlichen Ansichten — `SurveyPublicView`, `LesAnsicht`,
 * `WithdrawalPage`, `HelferLayout` und die Anmeldemasken tragen sie alle.
 *
 * Die Inhalte pflegt der Betreiber unter Verwaltung → Branding; die Seiten selbst liegen
 * unter `/impressum` und `/datenschutz`.
 */
// #608: Im fremden iframe (LES2) tragen /impressum und /datenschutz SAMEORIGIN — dort im neuen Tab öffnen, sonst bliebe
// der Rahmen leer und die Rechtslinks unerreichbar.
export const rechtslinkZiel = (): { target?: string; rel?: string } =>
  window.self !== window.top ? { target: '_blank', rel: 'noopener' } : {};

export const PublicLegalFooter: React.FC<{ farbe?: string }> = ({ farbe = theme.textMuted }) => (
  <div style={{ marginTop: 20, fontSize: '.8rem', color: farbe, textAlign: 'center' }}>
    <a href="/impressum" {...rechtslinkZiel()} style={{ color: farbe, textDecoration: 'underline' }}>Impressum</a>
    {' · '}
    <a href="/datenschutz" {...rechtslinkZiel()} style={{ color: farbe, textDecoration: 'underline' }}>Datenschutz</a>
  </div>
);

export default PublicBrandHeader;
